import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export const REFERRAL_STATUS_LABELS = {
  new: "New",
  under_review: "Under Review",
  information_required: "Information Required",
  accepted_for_assessment: "Accepted for Assessment",
  assessment_scheduled: "Assessment Scheduled",
  not_proceeding: "Not Proceeding",
  closed: "Closed",
};

const STATUS_STYLES = {
  new: "bg-sky-100 text-sky-800 border-transparent",
  under_review: "bg-amber-100 text-amber-800 border-transparent",
  information_required: "bg-amber-100 text-amber-800 border-transparent",
  accepted_for_assessment: "bg-ukgreen-light text-ukgreen border-transparent",
  assessment_scheduled: "bg-indigo-100 text-indigo-800 border-transparent",
  not_proceeding: "bg-slate-100 text-slate-600 border-transparent",
  closed: "bg-slate-100 text-slate-500 border-transparent",
};

function Row({ label, value, testid }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-800 break-words" data-testid={testid}>{value || "—"}</dd>
    </div>
  );
}

export default function ReferralTab({ child, reload }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("referrals:manage");
  const referral = child.latest_referral;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const openEdit = () => {
    setForm({
      referral_date: referral.referral_date || "",
      referrer_name: referral.referrer_name || "",
      referrer_organisation: referral.referrer_organisation || "",
      referrer_job_title: referral.referrer_job_title || "",
      referrer_email: referral.referrer_email || "",
      referrer_phone: referral.referrer_phone || "",
      council_reference: referral.council_reference || "",
      po_reference: referral.po_reference || "",
      reason: referral.reason || "",
      requested_service: referral.requested_service || "",
      information_provided: referral.information_provided || "",
      priority: referral.priority || "standard",
      status: referral.status || "new",
      notes: referral.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { child_id: child.id };
      Object.entries(form).forEach(([k, v]) => {
        payload[k] = v === "" ? null : v;
      });
      await api.patch(`/referrals/${referral.id}`, payload);
      toast.success("Referral updated");
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!referral) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-slate-600" data-testid="referral-empty">
        No referral record on this case.
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="referral-tab">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg flex items-center gap-3">
          Referral <span className="font-mono text-sm text-slate-500">{referral.referral_number}</span>
          <Badge className={STATUS_STYLES[referral.status]} data-testid="referral-status-badge">
            {REFERRAL_STATUS_LABELS[referral.status] || referral.status}
          </Badge>
        </CardTitle>
        {canManage && (
          <Button variant="outline" className="h-10" data-testid="referral-edit-button" onClick={openEdit}>
            <Pencil className="h-4 w-4 mr-2" /> Edit referral
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Row label="Referral date" value={referral.referral_date} testid="referral-date" />
          <Row label="Priority" value={referral.priority} testid="referral-priority" />
          <Row label="Council reference" value={referral.council_reference} testid="referral-council-ref" />
          <Row label="PO / authorisation reference" value={referral.po_reference} testid="referral-po-ref" />
          <Row label="Referrer name" value={referral.referrer_name} testid="referral-referrer" />
          <Row label="Referrer organisation" value={referral.referrer_organisation} />
          <Row label="Referrer job title" value={referral.referrer_job_title} />
          <Row label="Referrer email" value={referral.referrer_email} />
          <Row label="Referrer telephone" value={referral.referrer_phone} />
          <div className="sm:col-span-2 lg:col-span-3"><Row label="Reason for referral" value={referral.reason} testid="referral-reason" /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Row label="Requested service" value={referral.requested_service} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Row label="Relevant information provided" value={referral.information_provided} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Row label="Notes" value={referral.notes} /></div>
        </dl>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit referral {referral.referral_number}</DialogTitle>
              <DialogDescription>Changes are audit-logged with previous and new values.</DialogDescription>
            </DialogHeader>
            {form && (
              <form onSubmit={save} className="space-y-4" data-testid="referral-edit-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="referral-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.entries(REFERRAL_STATUS_LABELS).map(([k, l]) => (
                          <SelectItem key={k} value={k} data-testid={`referral-status-option-${k}`}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="referral-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Referral date</Label>
                    <Input type="date" value={form.referral_date} onChange={(e) => setForm({ ...form, referral_date: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Council reference</Label>
                    <Input value={form.council_reference} onChange={(e) => setForm({ ...form, council_reference: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>PO / authorisation reference</Label>
                    <Input data-testid="referral-po-input" value={form.po_reference} onChange={(e) => setForm({ ...form, po_reference: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Referrer name</Label>
                    <Input value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Referrer organisation</Label>
                    <Input value={form.referrer_organisation} onChange={(e) => setForm({ ...form, referrer_organisation: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Referrer job title</Label>
                    <Input value={form.referrer_job_title} onChange={(e) => setForm({ ...form, referrer_job_title: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Referrer email</Label>
                    <Input type="email" value={form.referrer_email} onChange={(e) => setForm({ ...form, referrer_email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Referrer telephone</Label>
                    <Input value={form.referrer_phone} onChange={(e) => setForm({ ...form, referrer_phone: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Reason for referral</Label>
                  <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Requested service</Label>
                  <Input value={form.requested_service} onChange={(e) => setForm({ ...form, requested_service: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Relevant information provided</Label>
                  <Textarea rows={2} value={form.information_provided} onChange={(e) => setForm({ ...form, information_provided: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button data-testid="referral-save-button" type="submit" disabled={saving}
                    className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
